// ─── Track Event Metadata ───────────────────────────────────────────────────

export interface TrackEvent {
  id: string
  name: string
  category: EventCategory
  distance?: number // in meters
  isFieldEvent: boolean
}

export enum EventCategory {
  SPRINTS = 'sprints',
  DISTANCE = 'distance',
  HURDLES = 'hurdles',
  JUMPS = 'jumps',
  THROWS = 'throws',
  MULTI_EVENTS = 'multi_events',
}

// ─── Personal Info (includes parent/guardian) ───────────────────────────────

export type GenderIdentity = 'male' | 'female' | 'non-binary' | 'other' | 'prefer-not-to-say'

export type CompetitionTeam = 'men' | 'women'

export interface PersonalInfo {
  firstName: string
  lastName: string
  email: string
  mobilePhone: string
  country: string
  address: {
    line1: string
    line2?: string
    city: string
    zipCode: string
    state: string
  }
  dateOfBirth: Date
  /**
   * Self-described gender identity for the student.
   * Used for display only; eligibility / grouping should use `competitionTeam`.
   */
  genderIdentity: GenderIdentity
  /**
   * Optional free-text details when genderIdentity is 'other'.
   */
  genderIdentityDetails?: string
  /**
   * Binary competition team selection used for filters, grouping, etc.
   */
  competitionTeam: CompetitionTeam
  profilePhoto?: string
  bio?: string
  parentGuardian: {
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
  }
}

// ─── Academic Info ──────────────────────────────────────────────────────────

export interface AcademicInfo {
  highSchool: string
  graduationYear: number
  gpa: number
  satScore?: number
  actScore?: number
  academicAchievements?: string[]
  intendedMajor?: string
  resumeUrl?: string
}

// ─── Athletic Info (display-only; PRs live in PersonalRecord) ───────────────

export interface AthleticInfo {
  ncaaId?: string
  height: {
    feet: number
    inches: number
  }
  position?: string
  primaryEvents: string[] // event IDs referencing TRACK_EVENTS, e.g. "100m", "high_jump"
  recruitingVideoLink?: string
  clubTeam?: string
  clubPosition?: string
  plannedFirstYearOfCollege: number
  achievements?: Achievement[]
  videoLinks?: string[]
  coachReferences?: CoachReference[]
  trainingLogs?: TrainingLog[]
}

// ─── Student Profile (API shape) ────────────────────────────────────────────

export interface StudentProfile {
  id: string
  userId: string
  personalInfo: PersonalInfo
  academicInfo: AcademicInfo
  athleticInfo: AthleticInfo
  personalRecords?: PersonalRecord[] // joined from personal_records table
  profileCompletion: number
  isPublic: boolean
  hasAccess?: boolean // true after one-time payment or when in first 1,000
  createdAt: Date
  updatedAt: Date
}

// ─── Personal Record (own table, normalized) ────────────────────────────────

export interface PersonalRecord {
  id: string
  studentId: string
  eventId: string // references TRACK_EVENTS id, e.g., "100m"
  mark: string // display string, e.g., "10.45" or "6'2\""
  value: number // sortable number (seconds for track, meters for field)
  date: Date
  venue: string
  isVerified: boolean
  isOutdoor?: boolean
}

/**
 * Frontend-only derived type combining event metadata with the user's PR.
 * Built at render time by matching TRACK_EVENTS entries to PersonalRecord rows.
 */
export interface EventWithRecord {
  event: TrackEvent
  personalRecord?: PersonalRecord
}

// ─── Supporting Types ───────────────────────────────────────────────────────

export interface Achievement {
  id: string
  title: string
  description: string
  date: Date
  level: AchievementLevel
  eventId?: string
}

export enum AchievementLevel {
  SCHOOL = 'school',
  DISTRICT = 'district',
  REGIONAL = 'regional',
  STATE = 'state',
  NATIONAL = 'national',
  INTERNATIONAL = 'international',
}

export interface CoachReference {
  id: string
  name: string
  title: string
  school: string
  email: string
  phone: string
  relationship: string
}

export interface TrainingLog {
  id: string
  date: Date
  workout: string
  duration?: number // in minutes
  notes?: string
}
